<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ProductBlock from '../parts/ProductBlock.vue';
import StarRating from '../parts/StarRating.vue';
import { formatDateTime } from '../helpers';

const props = defineProps({
  source: { type: Object, required: true },
  platform: { type: String, default: '' },
});

const { t, locale } = useI18n();

const feedback = computed(() => props.source.feedback || {});
const product = computed(() => props.source.product || {});

const sections = computed(() =>
  [
    {
      key: 'pros',
      label: t('MULTICHAT.SOURCE.FEEDBACK.PROS'),
      text: feedback.value.pros,
      tone: 'text-n-teal-11',
      icon: 'i-lucide-thumbs-up',
    },
    {
      key: 'cons',
      label: t('MULTICHAT.SOURCE.FEEDBACK.CONS'),
      text: feedback.value.cons,
      tone: 'text-n-ruby-11',
      icon: 'i-lucide-thumbs-down',
    },
    {
      key: 'text',
      label: t('MULTICHAT.SOURCE.FEEDBACK.COMMENT'),
      text: feedback.value.text,
      tone: 'text-n-slate-11',
      icon: 'i-lucide-message-square-text',
    },
  ].filter(section => section.text)
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <ProductBlock v-if="product.name" :product="product" :platform="platform" />
    <div
      class="flex flex-col gap-2.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
    >
      <div class="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <StarRating
          v-if="feedback.rating"
          :rating="Number(feedback.rating)"
          size="md"
        />
        <span
          v-if="feedback.purchase_status"
          class="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-n-teal-3 text-n-teal-11 text-[11px] font-semibold"
        >
          <span class="i-lucide-badge-check size-3" />
          {{ feedback.purchase_status }}
        </span>
        <span
          v-if="feedback.is_edited"
          class="inline-flex items-center h-5 px-1.5 rounded-md bg-n-amber-3 text-n-amber-11 text-[11px] font-medium"
        >
          {{ t('MULTICHAT.SOURCE.FEEDBACK.EDITED') }}
        </span>
        <span
          v-if="feedback.photos"
          class="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-n-alpha-2 text-n-slate-11 text-[11px] font-medium"
        >
          <span class="i-lucide-image size-3" />
          {{ t('MULTICHAT.SOURCE.PHOTOS', { count: feedback.photos }) }}
        </span>
        <span class="flex-1" />
        <span v-if="feedback.created_at" class="text-[11px] text-n-slate-10">
          {{ formatDateTime(feedback.created_at, locale) }}
        </span>
      </div>
      <div
        v-for="section in sections"
        :key="section.key"
        class="flex flex-col gap-0.5"
      >
        <span
          class="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide"
          :class="section.tone"
        >
          <span class="size-3" :class="section.icon" />
          {{ section.label }}
        </span>
        <p
          class="m-0 text-[13px] leading-5 text-n-slate-12 whitespace-pre-line"
        >
          {{ section.text }}
        </p>
      </div>
      <div v-if="feedback.order_id" class="text-[11px] text-n-slate-10">
        {{ t('MULTICHAT.SOURCE.ORDER') }}
        <span class="font-mono text-n-slate-11">{{ feedback.order_id }}</span>
      </div>
    </div>
  </div>
</template>
